export const CNPJ_LENGTH = 14;

const CNPJ_MASK_CHARS_REGEX = /[.\-/\s]/g;
const CNPJ_VALID_CHAR_REGEX = /^[0-9A-Za-z]$/;

export function normalizeCnpj(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }

  return value.trim().replace(CNPJ_MASK_CHARS_REGEX, "").toUpperCase();
}

export function isValidCnpjCharacter(char: string): boolean {
  return CNPJ_VALID_CHAR_REGEX.test(char);
}

export function countUsefulCnpjChars(value: string | null | undefined): number {
  if (value == null || value === "") {
    return 0;
  }

  return normalizeCnpj(value).replace(/[^0-9A-Z]/g, "").length;
}

export function extractValidCnpjChars(value: string | null | undefined): string {
  const normalized = normalizeCnpj(value);
  return normalized.replace(/[^0-9A-Z]/g, "").slice(0, CNPJ_LENGTH);
}

export function formatCnpj(value: string | null | undefined): string {
  const chars = extractValidCnpjChars(value);

  if (!chars) {
    return "";
  }

  const segments = [
    chars.slice(0, 2),
    chars.slice(2, 5),
    chars.slice(5, 8),
    chars.slice(8, 12),
    chars.slice(12, 14),
  ];

  let formatted = segments[0];

  if (segments[1]) {
    formatted += `.${segments[1]}`;
  }
  if (segments[2]) {
    formatted += `.${segments[2]}`;
  }
  if (segments[3]) {
    formatted += `/${segments[3]}`;
  }
  if (segments[4]) {
    formatted += `-${segments[4]}`;
  }

  return formatted;
}

export function hasInvalidCnpjCharacters(value: string | null | undefined): boolean {
  if (value == null || value === "") {
    return false;
  }

  const withoutMask = value.trim().replace(CNPJ_MASK_CHARS_REGEX, "");
  return withoutMask.split("").some((char) => !isValidCnpjCharacter(char));
}

export function findInvalidCnpjCharacters(
  value: string | null | undefined
): string[] {
  if (value == null || value === "") {
    return [];
  }

  const withoutMask = value.trim().replace(CNPJ_MASK_CHARS_REGEX, "");
  return withoutMask.split("").filter((char) => !isValidCnpjCharacter(char));
}

const CNPJ_CHECK_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_BODY_REGEX = /^[A-Z0-9]{12}$/;
const CNPJ_REGEX = /^[A-Z0-9]{12}\d{2}$/;

export function isValidCnpj(value: string | null | undefined): boolean {
  const normalized = normalizeCnpj(value);

  if (
    normalized.length !== CNPJ_LENGTH ||
    normalized === "00000000000000" ||
    !CNPJ_REGEX.test(normalized)
  ) {
    return false;
  }

  const body = normalized.substring(0, 12);

  if (!CNPJ_BODY_REGEX.test(body)) {
    return false;
  }

  const informedCheckDigits = normalized.substring(12);
  const calculatedCheckDigits = calculateCnpjCheckDigits(body);

  return informedCheckDigits === calculatedCheckDigits;
}

function calculateCnpjCheckDigits(body: string): string {
  let sum1 = 0;
  let sum2 = 0;

  for (let i = 0; i < 12; i++) {
    const asciiDigit = body.charCodeAt(i) - 48;
    sum1 += asciiDigit * CNPJ_CHECK_DIGIT_WEIGHTS[i + 1];
    sum2 += asciiDigit * CNPJ_CHECK_DIGIT_WEIGHTS[i];
  }

  const dv1 = sum1 % 11 < 2 ? 0 : 11 - (sum1 % 11);
  sum2 += dv1 * CNPJ_CHECK_DIGIT_WEIGHTS[12];
  const dv2 = sum2 % 11 < 2 ? 0 : 11 - (sum2 % 11);

  return `${dv1}${dv2}`;
}
